/**
 * MKS-491 — dark mode UI pieces. Everything theme-related in Layout lives
 * here so moving the menu later is a one-line change.
 *
 * - ThemeController: null-rendering effect that applies the `dark` class.
 * - ThemeMenu: compact icon button + Light / Dark / System menu.
 * - AppHeader: slim header row that hosts the menu.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Bars3Icon,
  CheckIcon,
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { useThemeChoice, useThemeController, type ThemePreference } from '../../hooks/useTheme';
import { useFlag } from '../../services/flagsContext';
import { useOptionalSidebar } from './SidebarContext';

type IconType = typeof SunIcon;

interface ThemeOption {
  value: ThemePreference;
  label: string;
  Icon: IconType;
}

const LIGHT_OPTION: ThemeOption = { value: 'light', label: 'Light', Icon: SunIcon };

const OPTIONS: readonly ThemeOption[] = [
  LIGHT_OPTION,
  { value: 'dark', label: 'Dark', Icon: MoonIcon },
  { value: 'system', label: 'System', Icon: ComputerDesktopIcon },
];

/** Applies the theme class from the user's preference. Renders nothing. */
export function ThemeController() {
  useThemeController();
  return null;
}

export function ThemeMenu() {
  const { theme, setTheme } = useThemeChoice();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const current = OPTIONS.find((o) => o.value === theme) ?? LIGHT_OPTION;
  const CurrentIcon = current.Icon;

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Theme"
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Theme: ${current.label}`}
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded-md text-ms-text-secondary hover:text-ms-text hover:bg-ms-surface-2"
      >
        <CurrentIcon className="h-5 w-5" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Theme"
          className="absolute right-0 top-full mt-1 z-50 w-36 bg-ms-surface border border-ms-border rounded-md shadow-lg py-1"
        >
          {OPTIONS.map(({ value, label, Icon }) => {
            const active = value === theme;
            return (
              <button
                key={value}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setTheme(value);
                  setOpen(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-ms-text hover:bg-ms-surface-2"
              >
                <Icon className="h-4 w-4 text-ms-text-secondary" aria-hidden="true" />
                <span className="flex-1 text-left">{label}</span>
                {active && <CheckIcon className="h-4 w-4 text-ms-orange" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Layout has had no TopBar since quick task 260512-uat-batch (F-13), so this
 * row exists only for the MKS-491 menu. Moving the menu later is a one-line
 * change.
 */
export function AppHeader() {
  // MKS-538 — below `lg` the sidebar is an off-canvas drawer, so this row owns
  // the only affordance that opens it. `useOptionalSidebar` keeps the header
  // renderable with no SidebarProvider (its own test mounts it bare), and
  // `useFlag` is fail-closed, so with the flag off the header is byte-identical
  // to what it was.
  const sidebar = useOptionalSidebar();
  const mobileDrawerOn = useFlag('mks_538_mobile_sidebar_drawer');

  return (
    <header className="h-10 shrink-0 flex items-center justify-end gap-2 px-4 sm:px-6 lg:px-8 border-b border-ms-border bg-ms-surface">
      {mobileDrawerOn && sidebar && (
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={sidebar.mobileOpen}
          title="Open menu"
          onClick={() => sidebar.setMobileOpen(true)}
          className="mr-auto -ml-1 p-1.5 rounded-md text-ms-text-secondary hover:text-ms-text hover:bg-ms-surface-2 lg:hidden"
        >
          <Bars3Icon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
      <div className="relative">
        <ThemeMenu />
      </div>
    </header>
  );
}
